<template>
  <!-- Revisión contractual: solo lectura, se alimenta del Resumen ejecutivo (S03 hitos · S05 riesgos). -->
  <summary-card :title="$t('prm_dash_contract_title')" :subtitle="$t('prm_dash_contract_subtitle')" :mode="mode" chip="resumen">
    <template #icon><b-icon pack="fas" icon="file-contract" size="is-small"></b-icon></template>
    <template #extra>
      <span class="pen-pill" :class="penaltyTotal > 0 ? 'bad' : 'good'">
        {{ $t('prm_dash_contract_penalty_total') }}: {{ eur(penaltyTotal) }}
      </span>
    </template>

    <!-- Tira de KPIs -->
    <div class="strip">
      <div class="kpi"><span class="kpi-label">{{ $t('prm_dash_contract_milestones_done') }}</span><b>{{ contractDone }} / {{ contractMilestones.length }}</b></div>
      <div class="kpi"><span class="kpi-label">{{ $t('prm_dash_contract_penalizing') }}</span><b :class="{ bad: penalizingCount > 0 }">{{ penalizingCount }}</b></div>
      <div class="kpi"><span class="kpi-label">{{ $t('prm_dash_contract_penalties_applied') }}</span><b :class="{ bad: penaltyTotal > 0 }">{{ eur(penaltyTotal) }}</b></div>
      <div class="kpi"><span class="kpi-label">{{ $t('prm_dash_contract_significant_risks') }}</span><b :class="{ warn: significant > 0 }">{{ significant }}</b></div>
    </div>

    <!-- Hitos contractuales -->
    <div class="sub-head">
      <span class="sub-title">{{ $t('prm_summary_s03_contract_milestones') }}<source-chip v-if="mode" source="resumen"></source-chip></span>
      <span class="sub-note">{{ $t('prm_dash_contract_fulfilled') }}: {{ contractDone }} · {{ $t('prm_dash_contract_open') }}: {{ contractOpen }}</span>
    </div>
    <div class="tbl-wrap">
      <table>
        <thead><tr>
          <th class="idc">{{ $t('prm_summary_col_id') }}</th><th>{{ $t('prm_summary_col_description') }}</th>
          <th>{{ $t('prm_summary_s04_col_date') }}</th><th>{{ $t('prm_summary_col_owner') }}</th>
          <th>{{ $t('prm_summary_col_status') }}</th><th>{{ $t('prm_summary_s04_col_comments') }}</th>
        </tr></thead>
        <tbody>
          <tr v-for="(m, i) in contractMilestones" :key="'c' + i">
            <td class="idc">{{ i + 1 }}</td><td>{{ m.description || '-' }}</td>
            <td>{{ date(m.date) }}</td><td>{{ m.owner || '-' }}</td>
            <td><span class="badge" :class="'st-' + (m.status || 'open')">{{ status(m.status) }}</span></td>
            <td>{{ m.comments || '-' }}</td>
          </tr>
          <tr v-if="!contractMilestones.length"><td class="empty" colspan="6">{{ $t('prm_summary_empty') }}</td></tr>
        </tbody>
      </table>
    </div>

    <!-- Hitos penalizables -->
    <div class="sub-head">
      <span class="sub-title">{{ $t('prm_summary_s03_penalty_milestones') }}<source-chip v-if="mode" source="resumen"></source-chip></span>
      <span class="sub-note">{{ penalizingCount > 0 ? penalizingCount + ' ' + $t('prm_dash_contract_penalizing_word') : $t('prm_dash_contract_none_penalizing') }}</span>
    </div>
    <div class="tbl-wrap">
      <table>
        <thead><tr>
          <th class="idc">{{ $t('prm_summary_col_id') }}</th><th>{{ $t('prm_summary_col_description') }}</th>
          <th>{{ $t('prm_summary_s03_col_contract_date') }}</th><th>{{ $t('prm_summary_s03_col_compliance_date') }}</th>
          <th>{{ $t('prm_summary_s03_col_penalty') }}</th><th>{{ $t('prm_summary_s03_col_days') }}</th>
          <th class="r">{{ $t('prm_summary_s03_col_calculated') }}</th><th>{{ $t('prm_summary_s03_col_penalizing') }}</th>
          <th>{{ $t('prm_summary_s04_col_comments') }}</th>
        </tr></thead>
        <tbody>
          <tr v-for="(m, i) in penaltyMilestones" :key="'p' + i">
            <td class="idc">{{ i + 1 }}</td><td>{{ m.description || '-' }}</td>
            <td>{{ date(m.contractDate) }}</td><td>{{ date(m.complianceDate) }}</td>
            <td>{{ m.penalty || '-' }}</td>
            <td :class="{ bad: num(m.daysDiff) > 0 }">{{ daysDiff(m.daysDiff) }}</td>
            <td class="r" :class="{ bad: num(m.calculated) > 0 }">{{ m.calculated != null && m.calculated !== '' ? eur(m.calculated) : '-' }}</td>
            <td><span class="badge" :class="m.penalizing === 'yes' ? 'yn-bad' : 'yn-good'">{{ yn(m.penalizing) }}</span></td>
            <td>{{ m.comments || '-' }}</td>
          </tr>
          <tr v-if="!penaltyMilestones.length"><td class="empty" colspan="9">{{ $t('prm_summary_empty') }}</td></tr>
        </tbody>
      </table>
    </div>

    <!-- Riesgos y oportunidades (S05), ordenados por I×P -->
    <div class="sub-head">
      <span class="sub-title">{{ $t('prm_dash_contract_risks') }}<source-chip v-if="mode" source="resumen"></source-chip></span>
      <span class="sub-note">{{ $t('prm_dash_contract_risks_note') }}</span>
    </div>
    <div class="tbl-wrap">
      <table>
        <thead><tr>
          <th class="idc">{{ $t('prm_summary_col_id') }}</th><th>{{ $t('prm_summary_col_type') }}</th><th>{{ $t('prm_summary_col_description') }}</th>
          <th>{{ $t('prm_summary_col_impact') }}</th><th>{{ $t('prm_summary_col_probability') }}</th><th>{{ $t('prm_summary_col_score') }}</th>
          <th>{{ $t('prm_summary_col_action') }}</th><th>{{ $t('prm_summary_col_owner') }}</th>
          <th>{{ $t('prm_summary_col_target_date') }}</th><th>{{ $t('prm_summary_col_status') }}</th>
        </tr></thead>
        <tbody>
          <tr v-for="(r, i) in orderedRisks" :key="'r' + i" :class="{ sig: isSignificant(r) }">
            <td class="idc">{{ i + 1 }}</td>
            <td><span :class="r.type === 'opportunity' ? 'type-opportunity' : 'type-threat'">{{ type(r.type) }}</span></td>
            <td>{{ r.description || '-' }}</td>
            <td>{{ r.impact }}</td><td>{{ r.probability }}</td>
            <td><b :style="{ color: scoreHex(r) }">{{ score(r) }}</b></td>
            <td>{{ r.action || '-' }}</td><td>{{ r.owner || '-' }}</td>
            <td>{{ date(r.targetDate) }}</td>
            <td><span class="badge" :class="'st-' + (r.status || 'open')">{{ status(r.status) }}</span></td>
          </tr>
          <tr v-if="!orderedRisks.length"><td class="empty" colspan="10">{{ $t('prm_summary_no_risks') }}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="foot-note">
      <b-icon pack="fas" icon="clipboard-list" size="is-small"></b-icon>
      <span>{{ $t('prm_dash_contract_footer') }}</span>
    </div>
  </summary-card>
</template>

<script>
  import SummaryCard from '../summary/summary-card.vue'
  import SourceChip from '../summary/source-chip.vue'
  import { fmtEur, fmtDate } from '../shared/format'
  import { riskScore } from '../summary/summary-model'
  import { SIGNIFICANT_RISK } from './dashboard-model'

  export default {
    name: 'PrmDashContractReview',

    components: { SummaryCard, SourceChip },

    props: {
      doc: { type: Object, default: () => ({}) },
      penaltyTotal: { type: Number, default: 0 },
      mode: { type: Boolean, default: false },
    },

    computed: {
      s03 () { return this.doc.s03 || {} },
      contractMilestones () { return this.s03.contractMilestones || [] },
      penaltyMilestones () { return this.s03.penaltyMilestones || [] },
      risks () { return this.doc.s05 || [] },
      orderedRisks () { return [...this.risks].sort((a, b) => riskScore(b) - riskScore(a)) },

      contractDone () { return this.contractMilestones.filter((m) => m.status === 'closed').length },
      contractOpen () { return this.contractMilestones.length - this.contractDone },
      penalizingCount () { return this.penaltyMilestones.filter((m) => m.penalizing === 'yes').length },
      significant () { return this.risks.filter((r) => this.isSignificant(r)).length },
    },

    methods: {
      eur (v) { return fmtEur(v || 0) },
      date (v) { return v ? fmtDate(v) : '-' },
      num (v) { return Number(v) || 0 },
      score (r) { return riskScore(r) },
      isSignificant (r) { return r.status !== 'closed' && riskScore(r) >= SIGNIFICANT_RISK },
      // Color del I×P: significativo ámbar, resto tono neutro.
      scoreHex (r) { return this.isSignificant(r) ? '#b9770e' : '#232c3d' },
      daysDiff (v) {
        const n = Number(v)
        if (v == null || v === '' || isNaN(n)) return '-'
        return (n > 0 ? '+' : '') + n
      },
      status (code) { return this.$t('prm_summary_risk_status_' + (code || 'open')) },
      type (code) { return this.$t('prm_summary_risk_type_' + (code || 'threat')) },
      yn (code) { return code ? this.$t('prm_summary_' + code) : '-' },
    },
  }
</script>

<style lang="scss" scoped>
  .pen-pill {
    margin-left: auto; font-size: 9px; font-weight: 700; text-transform: none;
    padding: 2px 8px; border-radius: 999px; border: 1px solid;
  }
  .pen-pill.bad { background: #fdf3f2; color: #c0392b; border-color: #e6b8b2; }
  .pen-pill.good { background: #f3faf5; color: #1e8449; border-color: #cfe3d4; }

  .strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 8px; padding: 10px; border-bottom: 1px solid #eef0f3; }
  .kpi { border: 1px solid #eef0f3; background: #fafbfc; border-radius: 6px; padding: 7px 9px; }
  .kpi-label { display: block; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; color: #6b7787; margin-bottom: 3px; }
  .kpi b { font-size: 15px; font-weight: 700; color: #1e3046; font-variant-numeric: tabular-nums; }
  .kpi b.bad { color: #c0392b; }
  .kpi b.warn { color: #b9770e; }

  // Cabecera de cada sub-tabla: título (izq) + nota informativa (der).
  .sub-head {
    display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
    padding: 12px 8px 6px;
  }
  .sub-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #6b7787; }
  .sub-note { font-size: 10px; font-weight: 400; color: #aab2bd; white-space: nowrap; }

  .tbl-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  // Todo a la izquierda (cabecera y celdas alineadas); solo la penalidad calculada a la derecha.
  th { background: #3d6b9a; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: .03em; font-weight: 700; padding: 5px 8px; text-align: left; white-space: nowrap; }
  th.r { text-align: right; }
  th.idc { width: 42px; }
  td { border-bottom: 1px solid #eef0f3; padding: 4px 8px; font-size: 11px; color: #2c3a4d; vertical-align: middle; white-space: nowrap; text-align: left; }
  td.idc { color: #3a6bc8; font-weight: 600; }
  td.r { text-align: right; font-variant-numeric: tabular-nums; }
  td.bad { color: #c0392b; font-weight: 600; }
  td.empty { text-align: center; color: #9aa4b2; padding: 10px; }

  // Cebra + hover (para revisar mejor); las filas significativas conservan su acento ámbar.
  tbody tr:nth-child(even):not(.sig) td { background: #f9fafb; }
  tbody tr:hover:not(.sig) td { background: #eef2f7; }
  // Fila significativa (I×P ≥ 12): fondo crema + acento naranja a la izquierda de la fila.
  tr.sig td { background: #fdf6ec; }
  tr.sig td:first-child { box-shadow: inset 3px 0 0 #d98c1f; }
  tr.sig:hover td { background: #fbeecb; }

  .badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; }
  .st-open { background: #fdecea; color: #c0392b; }
  .st-in_progress { background: #fdf3d7; color: #b7791f; }
  .st-closed { background: #e7f6ec; color: #1e8449; }
  .yn-bad { background: #fdecea; color: #c0392b; }
  .yn-good { background: #e7f6ec; color: #1e8449; }
  .type-threat { color: #c0392b; font-weight: 700; text-transform: uppercase; }
  .type-opportunity { color: #1e8449; font-weight: 700; text-transform: uppercase; }

  // Nota informativa sutil (misma que el encaje): icono verde de hoja de cálculo + texto gris.
  .foot-note {
    display: flex; align-items: flex-start; gap: 6px; padding: 7px 10px;
    font-size: 10px; color: #7a8798; line-height: 1.4; background: #f7faf8; border-top: 1px solid #eef0f3;
  }
  .foot-note ::v-deep .icon { color: #2e7d5b; flex-shrink: 0; }
</style>
